import React, { useState, useCallback } from 'react';
import {
    IndexTable,
    Card,
    useIndexResourceState,
    Text,
    useBreakpoints,
    Thumbnail,
    Badge,
    Button,
    ButtonGroup,
    Tooltip,
} from '@shopify/polaris';
import { ExternalSmallIcon } from '@shopify/polaris-icons';
import { dynamicResourceName, limit, redirectToAdmin } from '../../utils';
import ViewMetafield from './ViewMetafield';

const ProductVariantTable = ({
    productVariants,
    productVariantPageInfo,
    totalProductVariantsCount,
    onPaginationChange,
    isProductVariantLoading,
    productId,
    selectedResources,
    allResourcesSelected,
    handleSelectionChange
}) => {
    const [currentPage, setCurrentPage] = useState(1);

    const handleNext = useCallback(() => {
        if (productVariantPageInfo.hasNextPage && onPaginationChange) {
            setCurrentPage(prev => prev + 1);
            onPaginationChange({
                after: productVariantPageInfo.endCursor,
                before: null
            });
        }
    }, [productVariantPageInfo, onPaginationChange]);

    const handlePrevious = useCallback(() => {
        if (productVariantPageInfo.hasPreviousPage && onPaginationChange) {
            setCurrentPage(prev => prev - 1);
            onPaginationChange({
                after: null,
                before: productVariantPageInfo.startCursor
            });
        }
    }, [productVariantPageInfo, onPaginationChange]);

    const rowMarkup = productVariants.map(
        (
            { id, title, status, image, sku, metafields },
            index,
        ) => (
            <IndexTable.Row
                id={id}
                key={id}
                selected={selectedResources.includes(id)}
                position={index}
            >
                <IndexTable.Cell>
                    <Thumbnail size="small" source={image} />
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text variant="bodyMd" fontWeight="bold" as="span">
                        {id.split('/').pop()}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    {title}
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Badge tone={status === 'ACTIVE' ? 'success' : 'info'}>
                        {status}
                    </Badge>
                </IndexTable.Cell>
                <IndexTable.Cell>{sku}</IndexTable.Cell>
                <IndexTable.Cell>
                    <ButtonGroup gap='tight'>
                        <Tooltip content='View metafields'>
                            <ViewMetafield metafields={metafields} />
                        </Tooltip>
                        <Tooltip content='View variant'>
                            <Button
                                onClick={() => redirectToAdmin('variants', productId, id.split('/').pop())}
                                icon={ExternalSmallIcon}
                                variant='secondary'
                            ></Button>
                        </Tooltip>
                    </ButtonGroup>
                </IndexTable.Cell>
            </IndexTable.Row>
        ),
    );

    return (
        <React.Fragment>
            <div className='table'>
                <IndexTable
                    condensed={useBreakpoints().smDown}
                    resourceName={dynamicResourceName('product variant')}
                    itemCount={productVariants.length}
                    selectedItemsCount={
                        allResourcesSelected ? 'All' : selectedResources.length
                    }
                    onSelectionChange={handleSelectionChange}
                    selectable={true}
                    loading={isProductVariantLoading}
                    headings={[
                        { title: 'Image' },
                        { title: 'ID' },
                        { title: 'Title' },
                        { title: 'Status' },
                        { title: 'SKU' },
                        { title: 'Action' }
                    ]}
                    pagination={{
                        hasNext: currentPage < totalProductVariantsCount / limit,
                        onNext: handleNext,
                        label: `${(currentPage - 1) * limit + 1}–${Math.min(
                            currentPage * limit,
                            totalProductVariantsCount
                        )} of ${totalProductVariantsCount} product variants`,
                        hasPrevious: currentPage > 1,
                        onPrevious: handlePrevious,
                    }}
                >
                    {rowMarkup}
                </IndexTable>
            </div>
        </React.Fragment>
    );
};

export default ProductVariantTable;